export enum AbsoluteRoute {
    ROOT = "/",

    // Core
    INSIGHTS = "/insights",
    INSIGHTS_TAB = "/insights/:insightsTab",
    MONETIZATION = "/monetization",
    MONETIZATION_TAB = "/monetization/:monetizationTab",
    LENSES = "/lenses",
    LENS_FOLDERS = "/lens-folders",
    HOME = "/home",
    LENS = "/lens/:lensId",
    WEB_LENSES = "/:orgId/web-lenses",
    WEB_LENSES_SUBMIT = "/:orgId/web-lenses/submit",
    WEB_LENSES_UPDATE = "/:orgId/web-lenses/update/:lensId",
    WEB_LENSES_PUSH_TO_DEVICE = "/:orgId/web-lenses/push-to-device",
    ORG = "/:orgId",
    ORG_INSIGHTS = "/:orgId/insights",
    ORG_INSIGHTS_TAB = "/:orgId/insights/:insightsTab",
    ORG_MONETIZATION = "/:orgId/monetization",
    ORG_MONETIZATION_TAB = "/:orgId/monetization/:monetizationTab",
    ORG_LENSES = "/:orgId/lenses",
    ORG_LENS_FOLDERS = "/:orgId/lens-folders",
    ORG_LENS_FOLDER = "/:orgId/lens-folders/:lensFolderId",
    ORG_LENS_FOLDER_INSIGHTS = "/:orgId/lens-folders/:lensFolderId/insights",
    ORG_LENS_FOLDER_INSIGHTS_TAB = "/:orgId/lens-folders/:lensFolderId/insights/:insightsTab",
    ORG_LENS_FOLDER_MONETIZATION = "/:orgId/lens-folders/:lensFolderId/monetization",
    ORG_LENS_FOLDER_MONETIZATION_TAB = "/:orgId/lens-folders/:lensFolderId/monetization/:monetizationTab",
    ORG_LENS_FOLDER_LENSES = "/:orgId/lens-folders/:lensFolderId/lenses",
    ORG_LENS_FOLDER_LENS = "/:orgId/lens-folders/:lensFolderId/lenses/:lensId",
    ORG_ASSETS = "/:orgId/assets",
    ORG_ASSETS_CREATE = "/:orgId/assets/create",
    ORG_HOSTED_LENSES = "/:orgId/hosted-lenses",
    ORG_REMOTE_ASSETS = "/:orgId/remote-assets",
    ORG_REMOTE_ASSET_DETAIL = "/:orgId/remote-assets/:remoteAssetId",
    EMBED_REMOTE_ASSETS = "/embed/:orgId/remote-assets",
    EMBED_REMOTE_ASSET_DETAIL = "/embed/:orgId/remote-assets/:remoteAssetId",

    // Camera Kit - Core
    CK_ROOT = "/camera-kit/",
    CK_LENS_SCHEDULER = "/camera-kit/lens-scheduler",
    CK_APPS = "/camera-kit/apps",
    CK_EULA = "/camera-kit/eula",

    // Camera Kit
    ORG_CAMERA_KIT = "/:orgId/camera-kit",
    ORG_LENS_SCHEDULER = "/:orgId/camera-kit/lens-scheduler",
    ORG_LENS_SCHEDULER_GROUPS = "/:orgId/camera-kit/lens-scheduler/groups",
    ORG_LENS_SCHEDULER_GROUP = "/:orgId/camera-kit/lens-scheduler/groups/:groupId",
    ORG_LENS_SCHEDULER_GROUP_LENS = "/:orgId/camera-kit/lens-scheduler/groups/:groupId/lenses/:lensId/references/:reference/sources/:lensSourceType",
    ORG_LENS_SCHEDULER_LENSES = "/:orgId/camera-kit/lens-scheduler/lenses",
    ORG_LENS_SCHEDULER_LENS = "/:orgId/camera-kit/lens-scheduler/lenses/:lensId/references/:reference/sources/:lensSourceType",
    ORG_LENS_SCHEDULER_SOURCES = "/:orgId/camera-kit/lens-scheduler/sources",

    ORG_APPS = "/:orgId/camera-kit/apps",
    ORG_APPS_CREATE = "/:orgId/camera-kit/apps/create",
    ORG_APP = "/:orgId/camera-kit/apps/:appId",

    // Global
    WELCOME = "/welcome",
    WELCOME_ORG = "/welcome/:orgId",
    COMMUNITY_AUDIENCE = "/community-audience",

    // Submit
    SUBMIT = "/submit/:submissionId",
    SUBMIT_FLOW_REDIRECTOR = "/submit/:submissionId/flow-redirector",
    SUBMIT_LENS_FOLDER_SELECT = "/submit/:submissionId/lens-folders/select",
    SUBMIT_LENS_FOLDER_CREATE = "/submit/:submissionId/lens-folders/create",
    SUBMIT_LENS_SELECT = "/submit/:submissionId/lenses/select",
    SUBMIT_LENS_CREATE = "/submit/:submissionId/lenses/create",
    SUBMIT_LENS_UPDATE = "/submit/:submissionId/lenses/update/:lensId",
    SUBMIT_LENS_CREATE_SUCCESS = "/submit/:submissionId/lenses/success/:lensId",
    SUBMIT_ACCOUNT_SELECT = "/submit/:submissionId/accounts/select",

    // APIs
    APIS = "/apis",
    APIS_CREATE = "/apis/create",
    API = "/apis/:apiId",

    // Lens Studio
    LENS_STUDIO_REDIRECT = "/lens-studio/redirect",

    // Create Org + Lens Folder
    CREATE_ORG_LENS_FOLDER = "/create-org-lens-folder/:routeTo/:submissionId?",

    // Error
    ACCESS_DENIED = "/access-denied",
    NO_ROLE_ASSIGNED = "/no-role-assigned",
}

export interface RouteParams extends Record<AbsoluteRoute, Record<string, string>> {
    // Core
    [AbsoluteRoute.LENS]: {
        lensId: string;
    };
    [AbsoluteRoute.WELCOME_ORG]: {
        orgId: string;
    };
    [AbsoluteRoute.WEB_LENSES]: {
        orgId: string;
    };
    [AbsoluteRoute.WEB_LENSES_SUBMIT]: {
        orgId: string;
    };
    [AbsoluteRoute.WEB_LENSES_UPDATE]: {
        orgId: string;
        lensId: string;
    };
    [AbsoluteRoute.WEB_LENSES_PUSH_TO_DEVICE]: {
        orgId: string;
    };
    [AbsoluteRoute.ORG]: {
        orgId: string;
    };
    [AbsoluteRoute.INSIGHTS_TAB]: {
        insightsTab: string;
    };
    [AbsoluteRoute.ORG_INSIGHTS]: {
        orgId: string;
    };
    [AbsoluteRoute.ORG_INSIGHTS_TAB]: {
        orgId: string;
        insightsTab: string;
    };
    [AbsoluteRoute.ORG_LENSES]: {
        orgId: string;
    };
    [AbsoluteRoute.ORG_LENS_FOLDERS]: {
        orgId: string;
    };
    [AbsoluteRoute.ORG_LENS_FOLDER]: {
        orgId: string;
        lensFolderId: string;
    };
    [AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS]: {
        orgId: string;
        lensFolderId: string;
    };
    [AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS_TAB]: {
        orgId: string;
        lensFolderId: string;
        insightsTab: string;
    };
    [AbsoluteRoute.MONETIZATION_TAB]: {
        monetizationTab: string;
    };
    [AbsoluteRoute.ORG_MONETIZATION]: {
        orgId: string;
    };
    [AbsoluteRoute.ORG_MONETIZATION_TAB]: {
        orgId: string;
        monetizationTab: string;
    };
    [AbsoluteRoute.ORG_LENS_FOLDER_MONETIZATION]: {
        orgId: string;
        lensFolderId: string;
    };
    [AbsoluteRoute.ORG_LENS_FOLDER_MONETIZATION_TAB]: {
        orgId: string;
        lensFolderId: string;
        monetizationTab: string;
    };
    [AbsoluteRoute.ORG_LENS_FOLDER_LENSES]: {
        orgId: string;
        lensFolderId: string;
    };
    [AbsoluteRoute.ORG_LENS_FOLDER_LENS]: {
        orgId: string;
        lensFolderId: string;
        lensId: string;
    };
    [AbsoluteRoute.ORG_LENS_SCHEDULER_GROUP_LENS]: {
        orgId: string;
        groupId: string;
        lensId: string;
        reference: string;
        lensSourceType: string;
    };
    [AbsoluteRoute.ORG_LENS_SCHEDULER_LENS]: {
        orgId: string;
        lensId: string;
        reference: string;
        lensSourceType: string;
    };
    [AbsoluteRoute.ORG_APPS]: {
        orgId: string;
    };
    [AbsoluteRoute.ORG_APP]: {
        orgId: string;
        appId: string;
    };
    [AbsoluteRoute.ORG_APPS_CREATE]: {
        orgId: string;
    };

    [AbsoluteRoute.ORG_REMOTE_ASSETS]: {
        orgId: string;
    };

    [AbsoluteRoute.ORG_REMOTE_ASSET_DETAIL]: {
        orgId: string;
        remoteAssetId: string;
    };

    [AbsoluteRoute.EMBED_REMOTE_ASSETS]: {
        orgId: string;
    };

    [AbsoluteRoute.EMBED_REMOTE_ASSET_DETAIL]: {
        orgId: string;
        remoteAssetId: string;
    };

    // Submit
    [AbsoluteRoute.SUBMIT]: {
        submissionId: string;
    };
    [AbsoluteRoute.SUBMIT_FLOW_REDIRECTOR]: {
        submissionId: string;
    };
    [AbsoluteRoute.SUBMIT_LENS_FOLDER_SELECT]: {
        submissionId: string;
    };
    [AbsoluteRoute.SUBMIT_LENS_FOLDER_CREATE]: {
        submissionId: string;
    };
    [AbsoluteRoute.SUBMIT_LENS_SELECT]: {
        submissionId: string;
    };
    [AbsoluteRoute.SUBMIT_LENS_CREATE]: {
        submissionId: string;
    };
    [AbsoluteRoute.SUBMIT_LENS_UPDATE]: {
        submissionId: string;
        lensId: string;
    };
    [AbsoluteRoute.SUBMIT_LENS_CREATE_SUCCESS]: {
        submissionId: string;
        lensId: string;
    };
    [AbsoluteRoute.SUBMIT_ACCOUNT_SELECT]: {
        submissionId: string;
    };

    // APIs
    [AbsoluteRoute.API]: {
        apiId: string;
    };

    // Create Org + Lens Folder
    [AbsoluteRoute.CREATE_ORG_LENS_FOLDER]: {
        routeTo: "lens-studio" | "submit" | "web-lens";
        submissionId?: string;
    };
}
